import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, TextField, TextArea, Section, EmptyState } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, Trash2, X, Save, Mail, Phone, RefreshCw } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Promoteurs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null); // full detail
  const [showForm, setShowForm] = useState(false);
  const canEdit = ["admin", "dirigeant", "secretaire_bo", "manager_commercial"].includes(user?.role);
  const canDelete = ["admin", "dirigeant"].includes(user?.role);

  const load = () => api.get("/promoteurs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const select = async (id) => {
    const { data } = await api.get(`/promoteurs/${id}`);
    setSelected(data);
  };

  const save = async () => {
    if (!selected) return;
    try {
      await api.put(`/promoteurs/${selected.id}`, {
        nom: selected.nom, email_principal: selected.email_principal,
        email_denonciation: selected.email_denonciation, email_option: selected.email_option,
        email_facture: selected.email_facture, email_facture_2: selected.email_facture_2, email_relance: selected.email_relance,
        contact_nom: selected.contact_nom, telephone: selected.telephone,
        taux_commission: selected.taux_commission === "" || selected.taux_commission === undefined ? null : Number(selected.taux_commission),
        notes: selected.notes,
      });
      toast.success("Promoteur mis à jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async () => {
    if (!selected || !window.confirm(`Supprimer le promoteur "${selected.nom}" ?`)) return;
    await api.delete(`/promoteurs/${selected.id}`);
    toast.success("Promoteur supprimé");
    setSelected(null);
    load();
  };

  const recalc = async () => {
    try {
      const { data } = await api.post("/promoteurs/recalc-commissions");
      toast.success(`Commissions recalculées sur ${data.updated} dossier(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-8" data-testid="promoteurs-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        title="Promoteurs"
        subtitle="Configuration · Adresses emails automatisées"
        actions={canEdit && (
          <div className="flex items-center gap-2">
            <SecondaryButton onClick={recalc} data-testid="recalc-commissions-button">
              <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Recalculer commissions</span>
            </SecondaryButton>
            <PrimaryButton onClick={() => setShowForm(true)} data-testid="new-promoteur-button">
              <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Nouveau promoteur</span>
            </PrimaryButton>
          </div>
        )}
      />

      <div className="bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm text-sm text-amber-900 mb-6">
        Configurez ici une adresse email <strong>par type d&apos;action</strong> pour chaque promoteur. Quand vous cliquerez « Envoyer » sur un modèle promoteur (dénonciation, option, facture, relance paiement), <strong>le destinataire sera pré-rempli automatiquement</strong> selon le type d&apos;action. Sans configuration spécifique, l&apos;email principal est utilisé.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Section title={`Liste (${items.length})`}>
            <div className="max-h-[600px] overflow-y-auto">
              {items.length === 0 ? <EmptyState icon={Building2} title="Aucun promoteur" description="Cliquez sur 'Nouveau promoteur' pour ajouter Bouygues, Nexity, Vinci, etc." /> :
                items.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => select(p.id)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${selected?.id === p.id ? "bg-slate-50 border-l-2 border-l-[#B8873A]" : ""}`}
                    data-testid={`promoteur-item-${p.id}`}
                  >
                    <div className="text-sm font-semibold text-[#111111]">{p.nom}</div>
                    <div className="text-xs text-slate-500 mt-1">{p.email_principal}</div>
                  </button>
                ))}
            </div>
          </Section>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-sm border border-slate-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Promoteur</div>
                  <h2 className="text-xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{selected.nom}</h2>
                </div>
                {canDelete && (
                  <SecondaryButton onClick={remove} className="!text-red-700 !border-red-300 hover:!bg-red-50" data-testid="delete-promoteur-btn">
                    <span className="flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</span>
                  </SecondaryButton>
                )}
              </div>

              <div className="space-y-4">
                <TextField label="Nom du promoteur *" value={selected.nom || ""} onChange={(e) => setSelected({ ...selected, nom: e.target.value })} disabled={!canEdit} testid="promo-nom" />

                <div className="border-t border-slate-200 pt-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Adresses emails</div>
                  <div className="space-y-3">
                    <TextField label="Email principal (fallback) *" type="email" value={selected.email_principal || ""} onChange={(e) => setSelected({ ...selected, email_principal: e.target.value })} disabled={!canEdit} placeholder="contact@bouygues-immobilier.com" testid="promo-email-principal" />
                    <TextField label="Email dénonciations" type="email" value={selected.email_denonciation || ""} onChange={(e) => setSelected({ ...selected, email_denonciation: e.target.value })} disabled={!canEdit} placeholder="Optionnel — laissera l'email principal si vide" testid="promo-email-denonciation" />
                    <TextField label="Email options" type="email" value={selected.email_option || ""} onChange={(e) => setSelected({ ...selected, email_option: e.target.value })} disabled={!canEdit} placeholder="Ex: options@bouygues-immobilier.com" testid="promo-email-option" />
                    <TextField label="Email facturation" type="email" value={selected.email_facture || ""} onChange={(e) => setSelected({ ...selected, email_facture: e.target.value })} disabled={!canEdit} placeholder="Ex: comptabilite@bouygues-immobilier.com" testid="promo-email-facture" />
                    <TextField label="Email facturation (2e service)" type="email" value={selected.email_facture_2 || ""} onChange={(e) => setSelected({ ...selected, email_facture_2: e.target.value })} disabled={!canEdit} placeholder="Optionnel — 2e destinataire des factures" testid="promo-email-facture-2" />
                    <TextField label="Email relances paiement" type="email" value={selected.email_relance || ""} onChange={(e) => setSelected({ ...selected, email_relance: e.target.value })} disabled={!canEdit} placeholder="Optionnel — fallback = email facturation" testid="promo-email-relance" />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Commission</div>
                  <TextField label="Taux de commission (%)" type="number" step="0.1" value={selected.taux_commission ?? ""} onChange={(e) => setSelected({ ...selected, taux_commission: e.target.value })} disabled={!canEdit} placeholder="Défaut 5% si vide" testid="promo-taux-commission" />
                  <p className="text-xs text-slate-500 mt-1">Appliqué au prix de vente. Modifier ce taux recalcule automatiquement les dossiers de ce promoteur.</p>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Contact</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label="Contact référent" value={selected.contact_nom || ""} onChange={(e) => setSelected({ ...selected, contact_nom: e.target.value })} disabled={!canEdit} testid="promo-contact-nom" />
                    <TextField label="Téléphone" value={selected.telephone || ""} onChange={(e) => setSelected({ ...selected, telephone: e.target.value })} disabled={!canEdit} testid="promo-telephone" />
                  </div>
                </div>

                <TextArea label="Notes internes" rows={3} value={selected.notes || ""} onChange={(e) => setSelected({ ...selected, notes: e.target.value })} disabled={!canEdit} testid="promo-notes" />

                {canEdit && (
                  <div className="flex justify-end">
                    <PrimaryButton onClick={save} data-testid="save-promoteur-btn"><span className="flex items-center gap-2"><Save className="w-4 h-4" /> Enregistrer</span></PrimaryButton>
                  </div>
                )}

                <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm">
                  <div className="font-semibold text-slate-700 mb-1">💡 Rappel des routages email</div>
                  Dénonciation → {selected.email_denonciation || selected.email_principal || "—"}<br />
                  Option → {selected.email_option || selected.email_principal || "—"}<br />
                  Facture → {selected.email_facture || selected.email_principal || "—"}{selected.email_facture_2 ? ` + ${selected.email_facture_2}` : ""}<br />
                  Relance paiement → {selected.email_relance || selected.email_facture || selected.email_principal || "—"}
                </div>
              </div>
            </div>
          ) : (
            <EmptyState icon={Building2} title="Sélectionnez un promoteur" description="Ou créez-en un nouveau via le bouton en haut à droite." />
          )}
        </div>
      </div>

      {showForm && <PromoteurForm onClose={() => setShowForm(false)} onCreated={(p) => { setShowForm(false); load(); select(p.id); toast.success("Promoteur créé"); }} />}
    </div>
  );
}

function PromoteurForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    nom: "", email_principal: "", email_denonciation: "", email_option: "",
    email_facture: "", email_facture_2: "", email_relance: "", contact_nom: "", telephone: "", taux_commission: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
      const { data } = await api.post("/promoteurs", payload);
      onCreated(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="promoteur-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>Nouveau promoteur</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <TextField label="Nom du promoteur *" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Bouygues Immobilier" testid="new-promo-nom" />
          <TextField label="Email principal *" type="email" required value={form.email_principal} onChange={(e) => setForm({ ...form, email_principal: e.target.value })} placeholder="contact@promoteur.fr" testid="new-promo-email" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label="Email dénonciations" type="email" value={form.email_denonciation} onChange={(e) => setForm({ ...form, email_denonciation: e.target.value })} testid="new-promo-denonc" />
            <TextField label="Email options" type="email" value={form.email_option} onChange={(e) => setForm({ ...form, email_option: e.target.value })} testid="new-promo-option" />
            <TextField label="Email facturation" type="email" value={form.email_facture} onChange={(e) => setForm({ ...form, email_facture: e.target.value })} testid="new-promo-facture" />
            <TextField label="Email facturation (2e service)" type="email" value={form.email_facture_2} onChange={(e) => setForm({ ...form, email_facture_2: e.target.value })} testid="new-promo-facture-2" />
            <TextField label="Email relances" type="email" value={form.email_relance} onChange={(e) => setForm({ ...form, email_relance: e.target.value })} testid="new-promo-relance" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Contact référent" value={form.contact_nom} onChange={(e) => setForm({ ...form, contact_nom: e.target.value })} testid="new-promo-contact" />
            <TextField label="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} testid="new-promo-tel" />
          </div>
          <TextField label="Taux de commission (%)" type="number" step="0.1" value={form.taux_commission} onChange={(e) => setForm({ ...form, taux_commission: e.target.value })} placeholder="Défaut 5% si vide" testid="new-promo-taux" />
          <TextArea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="new-promo-notes" />
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={onClose}>Annuler</SecondaryButton>
            <PrimaryButton type="submit" disabled={saving} data-testid="submit-promoteur-btn">{saving ? "…" : "Créer"}</PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
